/* eslint-disable react/no-multi-comp */
import React from 'react'
import { FormattedDate } from 'react-intl'
import {
  type DashTableColumns,
  type DashTableIntlProps,
} from 'legacy/dash-blocks/dash-table/utils/dashTable.types'
import { DashTableTextCell } from 'legacy/dash-blocks/dash-table-text-cell/DashTableTextCell'
import { dashPixels } from 'legacy/dash-blocks/foundation/spacing-units/spacingUnits.types'
import { StatusButton } from 'legacy/dash-components/status-button/StatusButton'
import { useDashIntl } from 'intl/intl.hooks'
import { slugToIntlKey } from 'legacy/sections/generated-reports/components/list/utils/generatedReports.utils'
import { type ReportRequestListItem } from 'legacy/sections/generated-reports/utils/generatedReports.types'
import { type ReportsReportViewsCsv } from '__codegen__/api'

type Props = {
  downloadReportView: (reportViewId: ReportsReportViewsCsv) => void
}

export const useGeneratedReportsTableContent = ({ downloadReportView }: Props) => {
  const intl = useDashIntl()
  const columns: DashTableColumns<ReportRequestListItem> = [
    {
      Header: intl.formatMessage({ id: 'generatedReports.list.header.name' }),
      id: 'name',
      minWidth: 300,
      Cell: ({
        row: {
          original: { name },
        },
      }) => <DashTableTextCell>{name}</DashTableTextCell>,
      paddingLeft: dashPixels.PX8,
    },
    {
      Header: intl.formatMessage({ id: 'generatedReports.list.header.dataset' }),
      id: 'dataset-name',
      minWidth: 300,
      Cell: ({
        row: {
          original: {
            report: { slug },
          },
        },
      }) => (
        <DashTableTextCell>
          {intl.formatMessage({
            id: slugToIntlKey(slug),
          })}
        </DashTableTextCell>
      ),
      paddingLeft: dashPixels.PX8,
    },
    {
      Header: intl.formatMessage({ id: 'generatedReports.list.header.requestedAt' }),
      id: 'updatedAt',
      width: 300,
      Cell: ({
        row: {
          original: { createdAt },
        },
      }) => <FormattedDate value={createdAt} />,
    },
    {
      Header: intl.formatMessage({ id: 'generatedReports.list.header.requestedBy' }),
      id: 'requester-name',
      minWidth: 300,
      Cell: ({
        row: {
          original: { enterpriseAccount },
        },
      }) => {
        if (!enterpriseAccount) return null

        const { givenName = '', surname = '' } = enterpriseAccount
        return <DashTableTextCell>{`${givenName} ${surname}`}</DashTableTextCell>
      },
      paddingLeft: dashPixels.PX8,
    },
    {
      Header: intl.formatMessage({ id: 'generatedReports.list.header.status' }),
      id: 'generationState',
      width: 200,
      Cell: ({
        row: {
          original: { generationState, reportViews },
        },
      }) => (
        <StatusButton onClick={() => downloadReportView(reportViews[0])} status={generationState} />
      ),
    },
  ]

  const labels: Pick<DashTableIntlProps, 'defaultErrorMessage'> = {
    defaultErrorMessage: intl.formatMessage({
      id: 'generatedReports.list.error.title',
    }),
  }

  return {
    columns,
    ...labels,
  }
}
